import React, { useState, useEffect } from 'react';
import { AlertCircle, MailCheck } from 'lucide-react';
import authServiceAdmin from '../authServiceAdmin';
import { useAdmin } from '../context/AdminContext';

export default function LoginPage() {
  const { setCurrentAdmin, setIsAuthenticated, setCurrentPage } = useAdmin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Étape 2FA (code reçu par e-mail)
  const [step, setStep] = useState('password'); // 'password' | 'code'
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const finishLogin = (result) => {
    setCurrentAdmin(result.user);
    setIsAuthenticated(true);
    setCurrentPage('dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await authServiceAdmin.login(email, password);
      if (result.success && result.requires_2fa) {
        // Mot de passe OK → code envoyé par e-mail
        setStep('code');
        setCode('');
        setResendIn(60);
      } else if (result.success) {
        // Appareil de confiance : pas de code à saisir
        finishLogin(result);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erreur lors de la connexion: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) {
      setError('Entrez le code à 6 chiffres reçu par e-mail');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await authServiceAdmin.verify2FA(email, code.trim(), trustDevice);
      if (result.success) {
        finishLogin(result);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Erreur de vérification: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    setError('');
    const r = await authServiceAdmin.resend2FA(email);
    if (r.success) setResendIn(60);
    else setError(r.error || 'Renvoi impossible');
  };

  return (
    <div className="auth-page-admin">
      <div className="auth-container-admin">
        <div className="auth-card-admin">
          <div className="auth-logo">
            {step === 'code'
              ? <MailCheck size={48} />
              : <img src="/logo192.png" alt="Mbiyo" style={{ width: 56, height: 56, objectFit: 'contain' }} />}
          </div>
          <h1>Mbiyo Admin</h1>
          <h2>{step === 'code' ? 'Vérification en deux étapes' : 'Connexion Administrateur'}</h2>
          {error && (
            <div className="error-message">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {step === 'password' ? (
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@mbiyo.app" required />
              </div>
              <div className="form-group">
                <label>Mot de passe</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              <button type="submit" className="btn-auth-admin" disabled={isLoading}>
                {isLoading ? '⏳ Connexion en cours...' : 'Se connecter'}
              </button>
              <p className="auth-switch">
                Pas encore de compte ?{' '}
                <span onClick={() => setCurrentPage('register')}>Créer un compte admin</span>
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <p style={{ fontSize: 14, color: '#5C636E', textAlign: 'center', marginBottom: 14 }}>
                Un code à 6 chiffres a été envoyé à <strong>{email}</strong>.<br />
                Il est valable 10 minutes.
              </p>
              <div className="form-group">
                <label>Code de vérification</label>
                <input
                  type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{ letterSpacing: 8, textAlign: 'center', fontSize: 22, fontWeight: 700 }}
                  required
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#414852', marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                Faire confiance à cet appareil pendant 30 jours
              </label>
              <button type="submit" className="btn-auth-admin" disabled={isLoading || code.length !== 6}>
                {isLoading ? '⏳ Vérification...' : 'Valider'}
              </button>
              <p className="auth-switch" style={{ marginTop: 12 }}>
                {resendIn > 0
                  ? <>Renvoyer le code dans {resendIn} s</>
                  : <span onClick={handleResend}>Renvoyer le code</span>}
                {' · '}
                <span onClick={() => { setStep('password'); setError(''); }}>Retour</span>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
